import React, { memo, useMemo } from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (limit: number) => void;
  className?: string;
  loading?: boolean;
  maxVisiblePages?: number;
  pageSizeOptions?: number[];
  locale?: string;
}

export const Pagination: React.FC<PaginationProps> = memo(
  ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,
    className = '',
    loading = false,
    maxVisiblePages = 5,
    pageSizeOptions = [10, 20, 50, 100],
    locale = 'ru-RU',
  }) => {
    const safeTotalItems = Math.max(0, totalItems);
    const safeTotalPages = Math.max(1, Math.floor(totalPages));
    const safeCurrentPage = Math.max(1, Math.min(Math.floor(currentPage || 1), safeTotalPages));

    if (totalItems === 0) {
      return null;
    }

    const visiblePages = useMemo(() => {
      const maxVisible = Math.max(3, Math.floor(maxVisiblePages));

      if (safeTotalPages <= maxVisible)
        return Array.from({ length: safeTotalPages }, (_, i) => i + 1);

      const pages: (number | 'ellipsis')[] = [];
      const half = Math.floor(maxVisible / 2);

      let start = safeCurrentPage - half;
      let end = safeCurrentPage + half;

      if (start < 1) {
        end += 1 - start;
        start = 1;
      }
      if (end > safeTotalPages) {
        start -= end - safeTotalPages;
        end = safeTotalPages;
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('ellipsis');
      }

      for (let i = start; i <= end; i++) pages.push(i);

      if (end < safeTotalPages) {
        if (end < safeTotalPages - 1) pages.push('ellipsis');
        pages.push(safeTotalPages);
      }

      return pages;
    }, [safeCurrentPage, safeTotalPages, maxVisiblePages]);

    const rangeStart = Math.min(
      safeTotalItems,
      Math.max(0, (safeCurrentPage - 1) * itemsPerPage) + 1,
    );
    const rangeEnd = Math.min(safeTotalItems, safeCurrentPage * itemsPerPage);

    const baseButton =
      'min-w-[40px] h-10 px-3 border rounded-md flex items-center justify-center text-sm font-medium transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed';

    return (
      <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
        {onItemsPerPageChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Показывать:</span>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              disabled={loading}
              aria-label="Количество элементов на странице"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="text-sm text-gray-600 whitespace-nowrap">
          {rangeStart}–{rangeEnd} из {safeTotalItems.toLocaleString(locale)}
        </div>

        <nav aria-label="Навигация по страницам" className="flex items-center gap-1">
          <button
            className={`${baseButton} text-gray-600 bg-white hover:bg-gray-50 border-gray-300`}
            onClick={() => onPageChange(1)}
            disabled={safeCurrentPage === 1 || loading}
            aria-label="Первая страница"
            title="Первая"
          >
            «
          </button>

          <button
            className={`${baseButton} text-gray-600 bg-white hover:bg-gray-50 border-gray-300`}
            onClick={() => onPageChange(Math.max(1, safeCurrentPage - 1))}
            disabled={safeCurrentPage === 1 || loading}
            aria-label="Предыдущая страница"
            title="Предыдущая"
          >
            ‹
          </button>

          {visiblePages.map((p, idx) =>
            p === 'ellipsis' ? (
              <span
                key={`e-${idx}`}
                className={`${baseButton} cursor-default text-gray-400 border-transparent`}
                aria-hidden
              >
                …
              </span>
            ) : (
              <button
                key={`p-${p}`}
                onClick={() => p !== safeCurrentPage && !loading && onPageChange(p)}
                disabled={p === safeCurrentPage || loading}
                aria-current={p === safeCurrentPage ? 'page' : undefined}
                aria-label={p === safeCurrentPage ? `Текущая страница, ${p}` : `Страница ${p}`}
                title={`Страница ${p}`}
                className={`${baseButton} ${p === safeCurrentPage ? 'bg-blue-600 text-white border-blue-600' : 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300'}`}
              >
                {p}
              </button>
            ),
          )}

          <button
            className={`${baseButton} text-gray-600 bg-white hover:bg-gray-50 border-gray-300`}
            onClick={() => onPageChange(Math.min(safeTotalPages, safeCurrentPage + 1))}
            disabled={safeCurrentPage === safeTotalPages || loading}
            aria-label="Следующая страница"
            title="Следующая"
          >
            ›
          </button>

          <button
            className={`${baseButton} text-gray-600 bg-white hover:bg-gray-50 border-gray-300`}
            onClick={() => onPageChange(safeTotalPages)}
            disabled={safeCurrentPage === safeTotalPages || loading}
            aria-label="Последняя страница"
            title="Последняя"
          >
            »
          </button>
        </nav>
      </div>
    );
  },
);

Pagination.displayName = 'Pagination';
